/**
 * usePageMeta — simple reactive page title & meta tag manager.
 * Replaces @unhead/vue; avoids Vue 3.5 internal compatibility issues.
 *
 * Usage:
 *   // Static
 *   usePageMeta({ title: 'Page — LeadRanks', meta: { description: '...' } })
 *
 *   // Reactive (getter function)
 *   usePageMeta(() => ({
 *     title: article.value?.title || 'LeadRanks',
 *     meta: { description: article.value?.excerpt || '...' },
 *   }))
 *
 *   // Page-specific JSON-LD (FAQPage, BreadcrumbList, etc.) — only inject
 *   // schema that matches content genuinely visible on the page; Google's
 *   // guidelines require structured data to reflect real page content, not
 *   // page-invisible/fabricated markup.
 *   usePageMeta({ jsonLd: [{ '@context': 'https://schema.org', '@type': 'FAQPage', ... }] })
 */
import { onMounted, onUnmounted, watch, unref } from 'vue';

const EXISTING_META = new Set();
let observer = null;

function upsertMeta(nameOrProperty, content, attr = 'name') {
  const selector = attr === 'property'
    ? `meta[property="${nameOrProperty}"]`
    : `meta[name="${nameOrProperty}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, nameOrProperty);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
  EXISTING_META.add(selector);
}

// Page-specific JSON-LD is fully page-owned (removed entirely on unmount,
// unlike meta tags which persist/get overwritten) — a route without its own
// jsonLd should never inherit the previous route's schema.
function clearPageJsonLd() {
  document.querySelectorAll('script[data-page-jsonld]').forEach((el) => el.remove());
}

function applyJsonLd(blocks) {
  clearPageJsonLd();
  if (!Array.isArray(blocks)) return;
  blocks.forEach((block) => {
    if (!block) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-page-jsonld', 'true');
    el.textContent = JSON.stringify(block);
    document.head.appendChild(el);
  });
}

function cleanupStale() {
  // Clean up meta tags that were set by previous pages but not refreshed
  const all = document.querySelectorAll('meta[name], meta[property]');
  all.forEach((el) => {
    const name = el.getAttribute('name');
    const prop = el.getAttribute('property');
    if (name === 'csrf-token' || name === 'viewport' || name === 'charset') return;
    if (prop?.startsWith('og:') || name === 'description' || name === 'twitter:card') return;
    // Keep non-standard metas; only clean ones we know about
  });
}

export function usePageMeta(getMeta) {
  let stopWatcher = null;

  const apply = () => {
    const input = typeof getMeta === 'function' ? getMeta() : getMeta;
    if (!input) return;
    const meta = unref(input);

    // Title
    document.title = meta.title || 'LeadRanks';

    // Description
    const desc = meta.meta?.description || meta.description || meta.meta?.find?.(m => m.name === 'description')?.content;
    if (desc) upsertMeta('description', desc);

    const setKeys = new Set();

    // Array-style meta
    if (Array.isArray(meta.meta)) {
      for (const m of meta.meta) {
        if (m.name) { upsertMeta(m.name, m.content, 'name'); setKeys.add(m.name); }
        if (m.property) { upsertMeta(m.property, m.content, 'property'); setKeys.add(m.property); }
      }
    }

    // Object-style meta
    if (meta.meta && !Array.isArray(meta.meta)) {
      for (const [key, val] of Object.entries(meta.meta)) {
        upsertMeta(key, val, 'name');
        setKeys.add(key);
      }
    }

    // Social-share fallbacks — every page gets a correct link-preview even
    // if it only bothered setting title/description. Explicit values above
    // always win (checked via setKeys); this only fills real gaps. Fixes a
    // real, live bug: every page in this app set og:title/og:description
    // but NONE set twitter:title/twitter:description, so Twitter/X (and
    // any client honoring twitter:* over og:* first) always showed
    // index.html's generic hardcoded defaults instead of the real page
    // title/description.
    const title = meta.title || document.title;
    if (title && !setKeys.has('og:title')) upsertMeta('og:title', title, 'property');
    if (desc && !setKeys.has('og:description')) upsertMeta('og:description', desc, 'property');
    if (title && !setKeys.has('twitter:title')) upsertMeta('twitter:title', title, 'name');
    if (desc && !setKeys.has('twitter:description')) upsertMeta('twitter:description', desc, 'name');
    if (!setKeys.has('twitter:card')) upsertMeta('twitter:card', 'summary_large_image', 'name');

    // Page-specific JSON-LD
    applyJsonLd(meta.jsonLd);
  };

  onMounted(() => {
    apply();
    if (typeof getMeta === 'function') {
      stopWatcher = watch(getMeta, apply, { deep: true, immediate: false });
    }
  });

  onUnmounted(() => {
    if (stopWatcher) stopWatcher();
    clearPageJsonLd();
  });
}
